const CLIENT_PORTAL_API_PREFIX = "/api/internal/client-portal/" as const;

export type ClientPortalPublicCacheRule = {
  path: string;
  methods: readonly string[];
};

/** Exact bootstrap exceptions that may retain SWR cache entries after a purge. */
export const CLIENT_PORTAL_PUBLIC_CACHE_ALLOWLIST: readonly ClientPortalPublicCacheRule[] =
  [
    {
      path: "/api/internal/client-portal/ssb/sign-up/",
      methods: ["POST"],
    },
    {
      path: "/api/internal/client-portal/ssb/sign-up-create-account/",
      methods: ["POST"],
    },
    {
      path: "/api/internal/client-portal/ssb/create-client-account/",
      methods: ["POST"],
    },
    {
      path: "/api/internal/client-portal/sso-config/",
      methods: ["GET"],
    },
  ];

export function normalizeClientPortalCacheKeyPath(key: string): string {
  const withoutQuery = key.split("?")[0]?.split("#")[0] ?? key;
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
}

function matchesAllowlistPath(pathname: string, rulePath: string): boolean {
  return pathname === rulePath;
}

export function isPublicClientPortalCacheKey(key: unknown): boolean {
  if (typeof key !== "string") {
    return false;
  }

  const pathname = normalizeClientPortalCacheKeyPath(key);
  if (!pathname.startsWith(CLIENT_PORTAL_API_PREFIX)) {
    return false;
  }

  return CLIENT_PORTAL_PUBLIC_CACHE_ALLOWLIST.some((rule) =>
    matchesAllowlistPath(pathname, rule.path)
  );
}

export function isProtectedClientPortalApiRequest(
  path: string,
  method: string
): boolean {
  const pathname = normalizeClientPortalCacheKeyPath(path);
  const normalizedMethod = method.toUpperCase();

  if (!pathname.startsWith(CLIENT_PORTAL_API_PREFIX)) {
    return false;
  }

  const isAllowlisted = CLIENT_PORTAL_PUBLIC_CACHE_ALLOWLIST.some(
    (rule) =>
      matchesAllowlistPath(pathname, rule.path) &&
      rule.methods.includes(normalizedMethod)
  );

  return !isAllowlisted;
}

export function isProtectedClientPortalCacheKey(key: unknown): boolean {
  if (typeof key !== "string") {
    return false;
  }

  if (key.startsWith("/api/internal/client-portal/me/")) {
    return true;
  }

  const pathname = normalizeClientPortalCacheKeyPath(key);
  if (!pathname.startsWith(CLIENT_PORTAL_API_PREFIX)) {
    return false;
  }

  return !isPublicClientPortalCacheKey(key);
}
